import { Box, Breakpoint, Button, CircularProgress, Grid, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import * as React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router";
import SolarLiveStatusImg from "../../logo/solar_live_status.png";
import { Path as RoutePath } from "../../path";
import { actionCreators, ApplicationState, FetchState, selectors } from "../../store";
import FetchHelper from "../FetchHelper";
import ja from 'dayjs/locale/ja';
import { formatNumber, formatNumberNullToHyphen } from "../../utils";
import useResponsive from "../../hooks/useResponsive";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import axios from 'axios';
import { Auth } from "aws-amplify";
import PageMinutes from "../common/PageMinutes";

// FETCH ID
const fetchId = "SOLAR_LIVE_STATUS";
const menusFetchId = 'MENUS_FETCH';

// フォーム定義
interface SolarLiveStatusModel {
    kwh_day_total: string,
    amount_day_total: string,
    kwh_month_total: string,
    amount_month_total: string,
    live_situation_info_array: [
        {
            mainid: string,
            plant_name: string,
            panel_capacity: string,
            current_kw: string,
            kwh_sum_by_day: string,
            amount_sum_by_day: string,
            kwh_sum_by_month: string,
            amount_sum_by_month: string,
        }
    ] | [],
}

// 初期値設定
const initialValue: SolarLiveStatusModel = {
    kwh_day_total: "",
    amount_day_total: "",
    kwh_month_total: "",
    amount_month_total: "",
    live_situation_info_array: []
}

// -------------
// Props
export type SolarLiveStatusProps = ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps;
// -------------
// Component
export const SolarLiveStatus = (props: SolarLiveStatusProps) => {
    const { onGetData, onError } = props;
    localStorage.setItem('lastActiveFormPath', RoutePath.SolarLiveStatus);
    const [solarLiveStatus, setSolarLiveStatus] = React.useState<SolarLiveStatusModel>(initialValue);
    const [fetchLoading, setFetchLoading] = React.useState(true);
    const [disableLink, setDisableLink] = React.useState(true);
    const [contractionsDisplay, setContractionsDisplay] = React.useState(false);
    const isMobile = useResponsive({ query: 'down', key: 'sm' as Breakpoint });
    
    React.useEffect(() => {
        Auth.currentSession().then(async (session) => {
            const url = process.env.REACT_APP_APIGW_URL + "/master/menus/get";
            const token = session.getIdToken().getJwtToken();
            const data = await axios.get(url,{
                headers: {
                    'Authorization': token,
                }
            })
            if(data.data){
                let codeRestrictionArray = data.data.code_restriction_array ?? []
                let isDisable = codeRestrictionArray.length > 0 && !codeRestrictionArray.includes('GASOLAR')
                setDisableLink(isDisable)
            }
        }).catch((err) => {
        });
    },[]);

    React.useEffect(() => {
        if(contractionsDisplay)
            localStorage.setItem('title', '太陽光ライブ（短縮版）');
        else 
            localStorage.setItem('title', '太陽光ライブ');
        window.dispatchEvent(new Event('local-storage'));
    },[contractionsDisplay]);

    // 取得正常終了時の初期表示
    const handleSuccess = (data: any) => {
        setCurrentTime(dayjs());
        setSolarLiveStatus(data);
        setFetchLoading(false);
    }

    const handleError = (success: boolean, data: any, error: any) => {
        onError(success, data, error);
        setFetchLoading(false);
    }

    //タイトルの高さを戻
    React.useEffect(() => {
        const params = new Map<string, any>();
        params.set('code', 'GAEX4');
        params.set('path', 'root');
        onGetData(params);
        return () => {
            let div: any = document.getElementsByClassName('css-ua3wu6')[0];
            if (typeof (div) !== 'undefined' && div != null) {
                div.removeAttribute("style");
            }
        };
        // eslint-disable-next-line
    }, []);

    const colHeaderStyle = {
        fontWeight: 'bold',
        border: '1px solid gray',
        textAlign: 'center' as 'center',
        backgroundColor: '#d9d9d9',
        height: '48px',
        position: 'relative',
        // アイコン分のスペースを常に確保
    }

    const colHeaderMobileStyle = {
        fontWeight: 'bold',
        border: '1px solid gray',
        textAlign: 'center' as 'center',
        backgroundColor: '#d9d9d9',
        height: '20px',
        fontSize: '0.6rem',
        position: 'relative',
    }

    const colStyle = {
        border: '1px solid gray',
        textAlign: 'right' as 'right',
        paddingRight: '10px',
        height: '44px',
    }

    let navigate = useNavigate();
    const handleOnclick = (mainid: string) => {
        sessionStorage.setItem('references', window.location.pathname);
        navigate(RoutePath.Gaex3 + "?mainid=" + mainid);
    }

    const [currentTime, setCurrentTime] = React.useState<any>(dayjs());
    const getNowTime = () => {
        dayjs.locale(ja);
        let now = currentTime;
        return (
            <span style={{ letterSpacing: '0.3em' }}>{now.locale('ja').format('YYYY年MM月DD日 (ddd) HH時mm分')}</span>
        );
    }

    // --- ソート状態 ---
    // 3つの状態: null（ソートなし）、asc（昇順）、desc（降順）
    const [sortConfig, setSortConfig] = React.useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);

    // ソート用のカラム定義
    const columns = [
        { key: 'plant_name', label: '発電所', isContraction:true},
        { key: 'panel_capacity', label: '発電容量' , isContraction:true},
        { key: 'current_kw', label: '現在電力' , isContraction:true},
        { key: 'kwh_sum_by_day', label: '当日発電量(日)' , isContraction:true},
        { key: 'amount_sum_by_day', label: '当日発電金額(日)' , isContraction:false},
        { key: 'kwh_sum_by_month', label: '当月発電量(月)' , isContraction:false},
        { key: 'amount_sum_by_month', label: '当月発電金額(月)' , isContraction:false},
    ];

    // ソートハンドラー
    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (!prev || prev.key !== key) {
                // 新しいカラムを昇順でソート
                return { key, direction: 'asc' };
            } else if (prev.direction === 'asc') {
                // 昇順の場合は降順に変更
                return { key, direction: 'desc' };
            } else if (prev.direction === 'desc') {
                // 降順の場合はソート解除
                return null;
            }
            return null;
        });
    };

    // データのソート処理
    const sortedData = React.useMemo(() => {
        if (!sortConfig) return solarLiveStatus.live_situation_info_array;
        const sorted = [...solarLiveStatus.live_situation_info_array].sort((a: any, b: any) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            // plant_name の場合は常に文字列として比較
            if (sortConfig.key === "plant_name") {
                // null/undefined 対応
                const aStr = aVal ?? '';
                const bStr = bVal ?? '';

                if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            }
            // 空または数値でない値を処理: 昇順の場合は先頭、降順の場合は末尾に配置
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);
            const aEmpty = aVal === undefined || aVal === null || aVal === '' || isNaN(aNum);
            const bEmpty = bVal === undefined || bVal === null || bVal === '' || isNaN(bNum);
            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return sortConfig.direction === 'asc' ? -1 : 1;
            if (bEmpty) return sortConfig.direction === 'asc' ? 1 : -1;
            // 数値で比較
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
            }
            // 数値でない場合は文字列で比較
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [solarLiveStatus.live_situation_info_array, sortConfig, disableLink]);

    const sortedByCurrent = React.useMemo(() => {
        const arr = Array.isArray(solarLiveStatus.live_situation_info_array)
            ? [...solarLiveStatus.live_situation_info_array]
            : [];

        const getRank = (value: any) => {
            if (value === "-" || value == null || value === "") {
                return 0;
            }

            const num = Number(value);

            if (!isNaN(num) && num === 0) {
                return 1;
            }

            return 2; // >=1
        };

        arr.sort((a: any, b: any) => {
            const rankDiff = getRank(a.current_kw) - getRank(b.current_kw);

            if (rankDiff !== 0) {
                return rankDiff;
            } 
            
            if (getRank(a.current_kw) === 2) {
                const aNum = Number(a.current_kw);
                const bNum = Number(b.current_kw);

                if (aNum !== bNum) {
                    return aNum - bNum;
                }
            }

            return (a.plant_name || "").localeCompare(
                b.plant_name || "",
                "ja"
            );
        });
        return arr;
    }, [solarLiveStatus.live_situation_info_array]);

    const chunkSize = Math.ceil((sortedByCurrent.length || 0) / 3);
    const chunk1 = sortedByCurrent.slice(0, chunkSize);
    const chunk2 = sortedByCurrent.slice(chunkSize, chunkSize * 2);
    const chunk3 = [
        ...sortedByCurrent.slice(chunkSize * 2),
        ...Array(Math.max(0, chunkSize * 3 - sortedByCurrent.length))
    ];

    // ソート用アイコン: 昇順は▲、降順は▼、アニメーションなし
    const SortIcon = ({ active, direction }: { active: boolean, direction?: 'asc' | 'desc' }) => {
        // アイコンのスペースを常に確保するため、非active時も空のspanを返す
        const iconSize = isMobile ? '0.55em' : '1em';
        const iconStyle = {
            verticalAlign: 'middle',
            height: iconSize,
            width: iconSize,
            display: 'inline-flex',
            alignItems: 'center',
            padding: 0
        };
        if (active && direction === 'asc') {
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, color: '#3b3b3b', verticalAlign: 'middle', height: iconSize, width: iconSize }}>
                    <ArrowUpwardIcon fontSize="small" sx={iconStyle} />
                </span>
            );
        } else if (active && direction === 'desc') {
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, color: '#3b3b3b', verticalAlign: 'middle', height: iconSize, width: iconSize }}>
                    <ArrowDownwardIcon fontSize="small" sx={iconStyle} />
                </span>
            );
        }
        // 非active時は空spanで幅を確保
        return <span style={{ display: 'inline-block', marginLeft: 4, height: iconSize, width: iconSize }} />;
    };

    // アニメーション用のkeyframes（現在は未使用）
    React.useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `@keyframes sort-bounce { 0% { transform: scaleY(1); } 30% { transform: scaleY(1.4); } 60% { transform: scaleY(0.8); } 100% { transform: scaleY(1); } }`;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    // テーブル行アニメーション用CSS（スライド＋フェード）を追加
    React.useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
        .row-slide-fade-anim {
            opacity: 0;
            transform: translateY(24px);
            animation: row-slide-fade-in 0.35s cubic-bezier(0.4,0,0.2,1) forwards;
        }
        @keyframes row-slide-fade-in {
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    const onRefreshGrid = () => {
        setFetchLoading(true);
        const params = new Map<string, any>();
        params.set('code', 'GAEX4');
        params.set('path', 'root');
        onGetData(params);
    }

    return (
        <div style={{ margin: 'auto', marginRight: isMobile ? '0px' :'10px', marginLeft: isMobile ? '0px' :'10px'}}>
            {fetchLoading && (
                <div style={{ top: "0px", left: '0px', position: "fixed", zIndex: 1200, width: '100%', height: '100%', padding: '50vh 50% 50% 50%', background: '#00000030' }}>
                    <CircularProgress />
                </div>
            )}
                <div>
                    <div>
                        {
                            !isMobile && 
                            <>
                                {
                                    !contractionsDisplay &&  <Stack direction='row' spacing={2} width="100%" margin="10px auto 0">
                                        <Grid item xs={6}>
                                            <Box component="img" src={SolarLiveStatusImg} sx={{ width: 140, height: 140 }} />
                                        </Grid>
                                        <Grid item xs={6} style={{ marginLeft: '50px', fontSize: 'large' }}>
                                            <table style={{ width: '100%' }} cellPadding={10}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ fontWeight: 'bold' }}>本日の発電状況</td>
                                                    </tr>
                                                    <tr>
                                                        <td>{getNowTime()}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>日合計発電量 {formatNumber(solarLiveStatus.kwh_day_total)}kWh ({formatNumber(solarLiveStatus.amount_day_total)}円)</td>
                                                    </tr>
                                                    <tr>
                                                        <td>月合計発電量 {formatNumber(solarLiveStatus.kwh_month_total)}kWh ({formatNumber(solarLiveStatus.amount_month_total)}円)</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </Grid>
                                    </Stack>
                                }
                                <div style={{ marginTop: '10px' }}>
                                    <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'right', marginBottom: '4px' }}>
                                        {
                                            contractionsDisplay && <>
                                                <Stack minWidth={"350px"} direction="row" justifyContent="center" alignItems="center" >
                                                    {getNowTime()}
                                                </Stack>
                                                <PageMinutes onRefreshGrid={onRefreshGrid}></PageMinutes>
                                            </>
                                        }
                                        <Button sx={{minWidth:"90px"}} variant="outlined" onClick={() => setContractionsDisplay(!contractionsDisplay)} style={{ marginLeft: 'auto', marginBottom: '4px' }}>
                                            {contractionsDisplay ? "通常表示" : "短縮表示"}
                                        </Button>
                                    </div>
                                    {
                                        !contractionsDisplay && 
                                            <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '4px' }}>
                                                <tbody>
                                                    <tr>
                                                        {columns.map(col => (
                                                            <td
                                                                key={col.key}
                                                                style={{ ...colHeaderStyle, cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                                                                onClick={() => handleSort(col.key)}
                                                            >
                                                                {col.label}
                                                                <SortIcon
                                                                    active={sortConfig?.key === col.key}
                                                                direction={sortConfig?.key === col.key ? sortConfig?.direction : undefined}
                                                            />
                                                        </td>
                                                    ))}
                                                    </tr>
                                                    {sortedData && sortedData.map((val, index) =>
                                                        <tr key={index} className="row-slide-fade-anim">
                                                            <td style={{
                                                                border: '1px solid gray',
                                                                paddingLeft: '10px',
                                                                height: '44px',
                                                                textAlign: 'left'
                                                            }}>
                                                                { disableLink ?
                                                                    <Typography>{val.plant_name}</Typography>
                                                                    : <Typography style={{ textDecoration: 'underline', cursor: 'pointer', color: 'blue' }} onClick={() => handleOnclick(val.mainid)}>{val.plant_name}</Typography>
                                                                }
                                                            </td>
                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.panel_capacity, "kW")}</td>
                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.current_kw, "kW")}</td>
                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.kwh_sum_by_day, "kWh")}</td>
                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.amount_sum_by_day, "円")}</td>
                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.kwh_sum_by_month, "kWh")}</td>
                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.amount_sum_by_month, "円")}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                    }
                                    {
                                        contractionsDisplay &&
                                        <Stack direction='row' spacing={1} width="100%">
                                            <Grid container spacing={1}>
                                                <Grid item xs={4} display="flex">
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '4px' }}>
                                                        <tbody>
                                                            <tr>
                                                                {columns.filter(col => col.isContraction).map(col => (
                                                                    <td
                                                                        key={col.key}
                                                                        style={{ ...colHeaderStyle, userSelect: 'none', position: 'relative' }}
                                                                    >
                                                                        {col.label}
                                                                </td>
                                                            ))}
                                                            </tr>
                                                            {chunk1 && chunk1.map((val, index) =>
                                                                <tr key={index} className="row-slide-fade-anim">
                                                                    <td style={{
                                                                        border: '1px solid gray',
                                                                        paddingLeft: '10px',
                                                                        height: '44px',
                                                                        textAlign: 'left'
                                                                    }}>
                                                                        { disableLink ?
                                                                            <Typography>{val.plant_name}</Typography>
                                                                            : <Typography style={{ textDecoration: 'underline', cursor: 'pointer', color: 'blue' }} onClick={() => handleOnclick(val.mainid)}>{val.plant_name}</Typography>
                                                                        }
                                                                    </td>
                                                                    <td style={colStyle}>{formatNumberNullToHyphen(val.panel_capacity, "kW")}</td>
                                                                    <td style={colStyle}>{formatNumberNullToHyphen(val.current_kw, "kW")}</td>
                                                                    <td style={colStyle}>{formatNumberNullToHyphen(val.kwh_sum_by_day, "kWh")}</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </Grid>
                                                <Grid item xs={4} display="flex">
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '4px' }}>
                                                        <tbody>
                                                            <tr>
                                                                {columns.filter(col => col.isContraction).map(col => (
                                                                    <td
                                                                        key={col.key}
                                                                        style={{ ...colHeaderStyle, userSelect: 'none', position: 'relative' }}
                                                                    >
                                                                        {col.label}
                                                                </td>
                                                            ))}
                                                            </tr>
                                                            {chunk2 && chunk2.map((val, index) =>
                                                                <tr key={index} className="row-slide-fade-anim">
                                                                    <td style={{
                                                                        border: '1px solid gray',
                                                                        paddingLeft: '10px',
                                                                        height: '44px',
                                                                        textAlign: 'left'
                                                                    }}>
                                                                        { disableLink ?
                                                                            <Typography>{val.plant_name}</Typography>
                                                                            : <Typography style={{ textDecoration: 'underline', cursor: 'pointer', color: 'blue' }} onClick={() => handleOnclick(val.mainid)}>{val.plant_name}</Typography>
                                                                        }
                                                                    </td>
                                                                    <td style={colStyle}>{formatNumberNullToHyphen(val.panel_capacity, "kW")}</td>
                                                                    <td style={colStyle}>{formatNumberNullToHyphen(val.current_kw, "kW")}</td>
                                                                    <td style={colStyle}>{formatNumberNullToHyphen(val.kwh_sum_by_day, "kWh")}</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </Grid>
                                                <Grid item xs={4} display="flex">
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '4px' }}>
                                                        <tbody>
                                                            <tr>
                                                                {columns.filter(col => col.isContraction).map(col => (
                                                                    <td
                                                                        key={col.key}
                                                                        style={{ ...colHeaderStyle, userSelect: 'none', position: 'relative' }}
                                                                    >
                                                                        {col.label}
                                                                </td>
                                                            ))}
                                                            </tr>
                                                            {chunk3 && chunk3.map((val, index) =>
                                                                <tr key={index} className="row-slide-fade-anim">
                                                                    {
                                                                        val 
                                                                        ?
                                                                        <>
                                                                            <td style={{
                                                                                border: '1px solid gray',
                                                                                paddingLeft: '10px',
                                                                                height: '44px',
                                                                                textAlign: 'left'
                                                                            }}>
                                                                                { disableLink ?
                                                                                    <Typography>{val.plant_name}</Typography>
                                                                                    : <Typography style={{ textDecoration: 'underline', cursor: 'pointer', color: 'blue' }} onClick={() => {
                                                                                            handleOnclick(val.mainid);
                                                                                    }}>{val.plant_name}</Typography>
                                                                                }
                                                                            </td>
                                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.panel_capacity, "kW")}</td>
                                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.current_kw, "kW")}</td>
                                                                            <td style={colStyle}>{formatNumberNullToHyphen(val.kwh_sum_by_day, "kWh")}</td>
                                                                        </>
                                                                        :
                                                                        <>
                                                                            <td style={{
                                                                                border: '1px solid gray',
                                                                                paddingLeft: '10px',
                                                                                height: '44px',
                                                                                textAlign: 'left'
                                                                            }}>
                                                                                    <Typography></Typography>
                                                                            </td>
                                                                            <td style={colStyle}></td>
                                                                            <td style={colStyle}></td>
                                                                            <td style={colStyle}></td>
                                                                        </>
                                                                    }
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </Grid>
                                            </Grid>
                                        </Stack>
                                    }
                                </div>
                            </>
                        }
                        {
                            isMobile && 
                            <>
                                <Stack direction='row' spacing={1} width="100%">
                                    <Grid item xs={4} display="flex" alignItems="center" justifyContent="center" >
                                        <Box component="img" src={SolarLiveStatusImg} sx={{ width: 80, height: 80 }} />
                                    </Grid>
                                    <Grid item xs={8} display="flex" alignItems="center" justifyContent="center" style={{margin:"auto"}}>
                                        <Typography variant="h2" textAlign="center">本日の発電状況</Typography>
                                    </Grid>
                                </Stack>
                                <Stack direction='column' spacing={1} width="100%">
                                    <Grid item xs={12} display="flex" alignItems="flex-start" justifyContent="flex-start">
                                        <Typography variant="h6" style={{fontWeight:"normal", fontSize: "0.95rem"}}  textAlign="left">
                                            {getNowTime()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} spacing={1} display="flex" alignItems="flex-start" justifyContent="flex-start">
                                        <Typography variant="h6" style={{fontWeight:"normal", fontSize: "0.95rem"}}  textAlign="left">
                                            日合計発電量 {formatNumber(solarLiveStatus.kwh_day_total)}kWh ({formatNumber(solarLiveStatus.amount_day_total)}円)
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} spacing={1} display="flex" alignItems="flex-start" justifyContent="flex-start">
                                        <Typography variant="h6" style={{fontWeight:"normal", fontSize: "0.95rem"}}  textAlign="left">
                                            月合計発電量 {formatNumber(solarLiveStatus.kwh_month_total)}kWh ({formatNumber(solarLiveStatus.amount_month_total)}円)
                                        </Typography>
                                    </Grid>
                                </Stack>
                                <div style={{ marginTop: '10px' }}>
                                    <table
                                        key={disableLink ? 'disable' : 'enable'}
                                        style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '4px' }}
                                    >
                                        <thead>
                                            <tr>
                                                {columns.map(col => (
                                                    <td
                                                        key={col.key}
                                                        style={{ ...colHeaderMobileStyle, cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                                                        onClick={() => handleSort(col.key)}
                                                    >
                                                        {col.label}
                                                        <SortIcon
                                                            active={sortConfig?.key === col.key}
                                                            direction={sortConfig?.key === col.key ? sortConfig?.direction : undefined}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedData && sortedData.map((val, index) =>
                                                <tr key={index} className="row-slide-fade-anim">
                                                    <td style={{
                                                        border: '1px solid gray',
                                                    }}>
                                                        { disableLink ?
                                                            <Typography style={{fontSize:'0.6rem'}}>{val.plant_name}</Typography>
                                                            : <Typography style={{ textDecoration: 'underline', cursor: 'pointer', color: 'blue', fontSize:'0.6rem' }} onClick={() => handleOnclick(val.mainid)}>{val.plant_name}</Typography>
                                                        }
                                                    </td>
                                                    <td style={{fontSize:'0.6rem',border: '1px solid gray',}}>{formatNumberNullToHyphen(val.panel_capacity, "kW")}</td>
                                                    <td style={{fontSize:'0.6rem',border: '1px solid gray',}}>{formatNumberNullToHyphen(val.current_kw, "kW")}</td>
                                                    <td style={{fontSize:'0.6rem',border: '1px solid gray',}}>{formatNumberNullToHyphen(val.kwh_sum_by_day, "kWh")}</td>
                                                    <td style={{fontSize:'0.6rem',border: '1px solid gray',}}>{formatNumberNullToHyphen(val.amount_sum_by_day, "円")}</td>
                                                    <td style={{fontSize:'0.6rem',border: '1px solid gray',}}>{formatNumberNullToHyphen(val.kwh_sum_by_month, "kWh")}</td>
                                                    <td style={{fontSize:'0.6rem',border: '1px solid gray',}}>{formatNumberNullToHyphen(val.amount_sum_by_month, "円")}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        }
                    </div>
                </div>
            <FetchHelper fetchId={fetchId} onComplete={(success, data, error) => success ? handleSuccess(data) : handleError(success, data, error)} />
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    fetchState: selectors.getFetchState(
        state,
        fetchId
    ),
    fetchStateMenu: selectors.getFetchState(
        state,
        menusFetchId
    ) as FetchState,
});

const mapDispatchToProps = {
    onGetData: (params: { [key: string]: any }) => actionCreators.fetch(fetchId, `/telemetry/get`, "GET", params),
    onError: (success: boolean, data: any, error: any) =>
        actionCreators.showMessage({
            type: "error",
            title: "太陽光ライブ状況",
            body: "太陽光ライブ状況一覧取得出来ません。",
        }),
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SolarLiveStatus as any);